<template>
  <div class="review-page">
    <div class="toolbar">
      <div class="title-block">
        <h2>评论奖励审核</h2>
        <span>审核通过后立即发放用户申请的奖励</span>
      </div>
      <div class="filters">
        <el-select v-model="query.status" placeholder="全部状态" clearable @change="search">
          <el-option label="待审核" :value="0" />
          <el-option label="已通过" :value="1" />
          <el-option label="已拒绝" :value="2" />
        </el-select>
        <el-button @click="loadData">刷新</el-button>
      </div>
    </div>

    <div class="table-panel">
      <el-table v-loading="loading" :data="rows" height="100%" stripe>
        <el-table-column prop="id" label="记录 ID" width="90" />
        <el-table-column label="用户" min-width="180">
          <template #default="scope">
            <div class="user-name">{{ scope.row.userName || "未设置用户名" }}</div>
            <div class="muted">ID {{ scope.row.userId }}<span v-if="scope.row.email"> · {{ scope.row.email }}</span></div>
          </template>
        </el-table-column>
        <el-table-column label="评论截图" width="130">
          <template #default="scope">
            <el-image
              class="screenshot"
              :src="scope.row.imageUrl"
              :preview-src-list="[scope.row.imageUrl]"
              preview-teleported
              fit="cover"
            >
              <template #error><div class="image-error">加载失败</div></template>
            </el-image>
          </template>
        </el-table-column>
        <el-table-column label="申请奖励" width="140">
          <template #default="scope">
            <el-tag :type="scope.row.rewardType === 1 ? 'warning' : 'success'" effect="plain">
              {{ rewardText(scope.row.rewardType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="提交时间" width="180">
          <template #default="scope">{{ formatDate(scope.row.createTime) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template #default="scope">
            <el-tag :type="statusType(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <div v-if="scope.row.status === 0" class="actions">
              <el-button type="success" size="small" :loading="processingId === scope.row.id" @click="audit(scope.row, 1)">发放</el-button>
              <el-button type="danger" plain size="small" :disabled="processingId !== null" @click="audit(scope.row, 2)">拒绝</el-button>
            </div>
            <span v-else class="muted">已处理</span>
          </template>
        </el-table-column>
        <template #empty><el-empty description="暂无评论奖励记录" /></template>
      </el-table>
    </div>

    <div class="pagination">
      <span>共 {{ total }} 条</span>
      <el-pagination
        v-model:current-page="query.pageNum"
        v-model:page-size="query.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="total"
        layout="sizes, prev, pager, next"
        @size-change="changeSize"
        @current-change="loadData"
      />
    </div>
  </div>
</template>

<script>
import { ElMessageBox } from "element-plus";
import { reviewReward } from "../../../api/index";

export default {
  name: "ReviewRewardTable",
  data() {
    return {
      loading: false,
      processingId: null,
      rows: [],
      total: 0,
      query: { status: null, pageNum: 1, pageSize: 20 },
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const response = await reviewReward.getList(this.query);
        if (response.code !== 200) throw new Error(response.msg || "加载失败");
        this.rows = response.data?.data || [];
        this.total = response.data?.total || 0;
      } catch (error) {
        console.error("加载评论奖励失败:", error);
        this.$message.error(error.message || "加载失败，请重试");
      } finally {
        this.loading = false;
      }
    },
    search() {
      this.query.pageNum = 1;
      this.loadData();
    },
    changeSize() {
      this.query.pageNum = 1;
      this.loadData();
    },
    async audit(row, status) {
      const approving = status === 1;
      const action = approving ? `发放 ${this.rewardText(row.rewardType)}` : "拒绝该申请";
      try {
        await ElMessageBox.confirm(`确定要${action}吗？`, approving ? "确认发放" : "确认拒绝", {
          confirmButtonText: approving ? "确认发放" : "确认拒绝",
          cancelButtonText: "取消",
          type: approving ? "warning" : "error",
        });
        this.processingId = row.id;
        const response = await reviewReward.audit({ id: row.id, status });
        if (response.code !== 200 || response.data !== true) throw new Error(response.msg || "操作失败");
        this.$message.success(approving ? `${this.rewardText(row.rewardType)}已发放` : "申请已拒绝");
        await this.loadData();
      } catch (error) {
        if (error !== "cancel" && error !== "close") {
          console.error("审核评论奖励失败:", error);
          this.$message.error(error.message || "操作失败，请重试");
        }
      } finally {
        this.processingId = null;
      }
    },
    rewardText(type) {
      return { 1: "7 天会员", 2: "50 罐头" }[type] || "未知奖励";
    },
    statusText(status) {
      return { 0: "待审核", 1: "已通过", 2: "已拒绝" }[status] || "未知";
    },
    statusType(status) {
      return { 0: "warning", 1: "success", 2: "danger" }[status] || "info";
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString("zh-CN", { hour12: false }) : "-";
    },
  },
};
</script>

<style scoped>
.review-page {
  box-sizing: border-box;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f7f7f5;
  color: #202124;
}
.toolbar, .pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.title-block h2 { margin: 0 0 5px; font-size: 22px; letter-spacing: 0; }
.title-block span, .muted { color: #777b80; font-size: 13px; }
.filters { display: flex; gap: 10px; }
.filters .el-select { width: 150px; }
.table-panel {
  min-height: 0;
  flex: 1;
  overflow: hidden;
  border: 1px solid #dedfdc;
  border-radius: 6px;
  background: #fff;
}
.user-name { margin-bottom: 4px; font-weight: 600; }
.screenshot { width: 88px; height: 64px; border-radius: 4px; cursor: zoom-in; }
.image-error { width: 100%; height: 100%; display: grid; place-items: center; background: #f2f3f1; color: #8b8e91; font-size: 12px; }
.actions { display: flex; gap: 8px; }
.pagination { min-height: 32px; color: #666; font-size: 14px; }
@media (max-width: 900px) {
  .review-page { padding: 12px; }
  .toolbar { align-items: flex-start; flex-direction: column; }
  .pagination { align-items: flex-start; flex-direction: column; }
}
</style>
